import { Component } from '@theme/component';

/**
 * Lightweight scroll-snap carousel used by the custom PDP sections
 * (before/after, ingredients, video reviews). Scrolling is native CSS scroll-snap;
 * this component only wires the prev/next buttons and pagination dots.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The horizontally scrolling list.
 * @property {HTMLElement[]} slides - The slide elements.
 * @property {HTMLButtonElement} [previous] - Previous button.
 * @property {HTMLButtonElement} [next] - Next button.
 * @property {HTMLButtonElement[]} [dots] - Pagination dots, one per slide.
 *
 * @extends Component<Refs>
 */
class ScrollCarouselComponent extends Component {
  requiredRefs = ['track', 'slides'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #bind() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.refs.track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    this.#update();
  }

  /** Scrolls back by one slide. */
  previous() {
    this.#step(-1);
  }

  /** Scrolls forward by one slide. */
  next() {
    this.#step(1);
  }

  /** @param {MouseEvent} event */
  goTo(event) {
    const dot = /** @type {HTMLElement} */ (event.target);
    const index = Number(dot.dataset.index);
    const slide = this.refs.slides[index];
    if (!slide) return;
    this.refs.track.scrollTo({ left: this.#offsetOf(slide), behavior: this.#behavior });
  }

  /** @param {number} direction */
  #step(direction) {
    const { track, slides } = this.refs;
    const first = slides[0];
    if (!first) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = first.getBoundingClientRect().width + gap;
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: distance * direction * rtl, behavior: this.#behavior });
  }

  /** @param {HTMLElement} slide */
  #offsetOf(slide) {
    return slide.offsetLeft - this.refs.track.offsetLeft;
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #update() {
    const { track, slides, previous, next, dots } = this.refs;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const scrollable = maxScroll > 2;

    this.toggleAttribute('data-scrollable', scrollable);
    if (previous) previous.disabled = !scrollable || position <= 2;
    if (next) next.disabled = !scrollable || position >= maxScroll - 2;

    if (!dots?.length) return;
    let active = 0;
    let closest = Infinity;
    slides.forEach((slide, index) => {
      const distance = Math.abs(Math.abs(this.#offsetOf(slide)) - position);
      if (distance < closest) {
        closest = distance;
        active = index;
      }
    });
    // When scrolled to the end, the last dot should be active even if the last slide can't snap to the start.
    if (scrollable && position >= maxScroll - 2) active = slides.length - 1;
    dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
  }
}

if (!customElements.get('scroll-carousel-component')) {
  customElements.define('scroll-carousel-component', ScrollCarouselComponent);
}
